import { useTranslation } from 'react-i18next';
import { Button, FilterToggleButton, PageHeader } from '../../../ui/index.js';
import { roleSubtitleKey, type RoleKind } from '../../requests/display/index.js';

interface RussiaRequestsListPageHeaderProps {
  role: RoleKind | null;
  narrow: boolean;
  canCreate: boolean;
  chipsOpen: boolean;
  onToggleChips: () => void;
  activeChipCount: number;
  chipsGroupId: string;
  filtersOpen: boolean;
  onToggleFilters: () => void;
  filtersActiveCount: number;
  filtersPanelId: string;
  // WHY (#1545): SIMPLE create entry point. Opens the editor in SIMPLE
  //   mode with the positions block.
  onCreateSimple: () => void;
  // WHY (#1546): COMPLEX create entry point. Opens the editor in COMPLEX
  //   mode with request-level totals + file attachments instead of
  //   positions.
  onCreateComplex: () => void;
}

/**
 * @aiPurpose Header row for `RussiaRequestsListPage` — title + role
 *   subtitle on the left, the chip-group "Statuses" toggle (#1507), the
 *   search "Filters" toggle (#1527) and the create button on the right.
 *   Mirrors the China `RequestsListPageHeader`. Pure presentation —
 *   gating + toggle state flow in from the page.
 */
export function RussiaRequestsListPageHeader({
  role,
  narrow,
  canCreate,
  chipsOpen,
  onToggleChips,
  activeChipCount,
  chipsGroupId,
  filtersOpen,
  onToggleFilters,
  filtersActiveCount,
  filtersPanelId,
  onCreateSimple,
  onCreateComplex,
}: RussiaRequestsListPageHeaderProps) {
  const { t } = useTranslation(['russiaRequests', 'requests']);
  return (
    <PageHeader
      title={t('russiaRequests:title')}
      subtitle={role ? t(`requests:${roleSubtitleKey(role)}`) : undefined}
      narrow={narrow}
      actions={
        <>
          <FilterToggleButton
            open={chipsOpen}
            activeCount={activeChipCount}
            icon="layout-list"
            label={t('requests:list.statusesToggle')}
            size={narrow ? 'md' : 'sm'}
            aria-controls={chipsGroupId}
            onClick={onToggleChips}
          />
          <FilterToggleButton
            open={filtersOpen}
            activeCount={filtersActiveCount}
            label={t('requests:list.filters')}
            size={narrow ? 'md' : 'sm'}
            aria-controls={filtersPanelId}
            onClick={onToggleFilters}
          />
          {canCreate && (
            <>
              <Button
                kind="primary"
                icon="plus"
                data-testid="russia-requests-new-simple-link"
                onClick={onCreateSimple}
              >
                {t('russiaRequests:actions.newSimple')}
              </Button>
              <Button
                kind="primary"
                icon="plus"
                data-testid="russia-requests-new-complex-link"
                onClick={onCreateComplex}
              >
                {t('russiaRequests:actions.newComplex')}
              </Button>
            </>
          )}
        </>
      }
    />
  );
}
